import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, ImagePlus, Eye, Globe, Newspaper, Sparkles, Loader2 } from "lucide-react";

const emptyForm = { title: "", excerpt: "", content: "", published: false };

export default function BlogManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const fileRef = useRef(null);

  const genAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/blog-draft", { topic: aiTopic.trim() });
      setForm((f) => ({ ...f, title: data.title || f.title, excerpt: data.excerpt || f.excerpt, content: data.content || f.content }));
      toast.success("AI ne article likh diya — padh kar edit zaroor karein, phir publish karein");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI abhi kaam nahi kar raha");
    } finally {
      setAiBusy(false);
    }
  };

  const load = () => api.get("/blog").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditPost(null);
    setForm(emptyForm);
    setImage(null);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditPost(p);
    setForm({ title: p.title, excerpt: p.excerpt, content: p.content, published: p.published });
    setImage(null);
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      let post;
      if (editPost) {
        ({ data: post } = await api.put(`/blog/${editPost.id}`, form));
      } else {
        ({ data: post } = await api.post("/blog", form));
      }
      if (image) {
        const fd = new FormData();
        fd.append("file", image);
        await api.post(`/blog/${post.id}/image`, fd);
      }
      toast.success(form.published ? "Post LIVE hai — website par dikh rahi hai" : "Draft save ho gaya (website par nahi dikhega)");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`"${p.title}" delete karein?`)) return;
    await api.delete(`/blog/${p.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-5" data-testid="blog-manager-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Blog Manager</h1>
          <p className="text-sm text-slate-500">Health articles likho — Google se free traffic aayega. Publish karte hi website par live.</p>
        </div>
        <Button onClick={openNew} data-testid="new-post-btn" className="bg-emerald-900 hover:bg-emerald-800"><Plus className="w-4 h-4 mr-2" /> New Post</Button>
      </div>

      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Post</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Views</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`blog-row-${p.id}`}>
                  <TableCell>
                    <div className="text-sm font-semibold">{p.title}</div>
                    <div className="text-xs text-slate-400 font-mono-num">/store/blog/{p.slug}</div>
                  </TableCell>
                  <TableCell data-testid={`blog-status-${p.id}`}>
                    {p.published ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><Globe className="w-3 h-3 mr-1" /> Live</Badge>
                    ) : (
                      <Badge className="bg-slate-200 text-slate-600 hover:bg-slate-200">Draft</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono-num text-sm"><span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5 text-slate-400" />{p.views}</span></TableCell>
                  <TableCell className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString("en-IN")}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" className="h-8" data-testid={`blog-edit-${p.id}`} onClick={() => openEdit(p)}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200" data-testid={`blog-delete-${p.id}`} onClick={() => remove(p)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400"><Newspaper className="w-8 h-8 mx-auto mb-2 text-slate-300" />Abhi koi post nahi — pehli post likho!</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="blog-dialog">
          <DialogHeader><DialogTitle className="font-display">{editPost ? "Edit Post" : "New Blog Post"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="border border-violet-200 bg-violet-50 rounded-xl p-3 space-y-2" data-testid="blog-ai-section">
              <Label className="text-xs font-semibold text-violet-800">AI se article likhwayein (optional)</Label>
              <div className="flex gap-2">
                <Input value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} placeholder="Topic — e.g. Bukhar mein kya khana chahiye" className="h-9 bg-white text-sm" data-testid="blog-ai-topic" />
                <Button type="button" onClick={genAi} disabled={aiBusy || !aiTopic.trim()} data-testid="blog-ai-generate-btn" className="bg-violet-700 hover:bg-violet-600 shrink-0 h-9">
                  {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Likho</>}
                </Button>
              </div>
              <p className="text-[11px] text-violet-500">AI draft likhega — aap padh kar edit karein, phir publish karein.</p>
            </div>
            <div>
              <Label className="text-xs">Title * (SEO ke liye achha title likho — e.g. "Kashipur mein dengue se kaise bachein")</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 h-10" data-testid="blog-title-input" />
            </div>
            <div>
              <Label className="text-xs">Short summary (Google search mein ye dikhti hai)</Label>
              <Input value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="mt-1 h-10" data-testid="blog-excerpt-input" placeholder="1-2 line mein post kis bare mein hai…" />
            </div>
            <div>
              <Label className="text-xs">Content * (normal text mein likho — har line break waise hi dikhega)</Label>
              <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={12} data-testid="blog-content-input"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setImage(e.target.files?.[0] || null)} />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="blog-image-btn">
                <ImagePlus className="w-3.5 h-3.5 mr-1.5" /> {image ? image.name : editPost?.cover_image ? "Cover image badlo" : "Cover image (optional)"}
              </Button>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="blog-published-toggle">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-emerald-700" />
                Website par LIVE karo
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form.title.trim() || !form.content.trim()} data-testid="blog-save-btn" className="bg-emerald-900 hover:bg-emerald-800">
              {busy ? "Saving…" : "Save Post"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
